'use client'

import Link from 'next/link'
import { Error } from '../Error'
import { useFormContext } from 'react-hook-form'

import { Checkbox as CheckboxUi } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { cn } from '@/utilities/ui'

import type { CheckboxField } from '@payloadcms/plugin-form-builder/types'
import type { FieldErrorsImpl, FieldValues, UseFormRegister } from 'react-hook-form'

// Seed convention: [link text](/path) in label string → render as Link
function labelWithLinks(str: string) {
  const parts: React.ReactNode[] = []
  const re = /\[([^\]]+)\]\(([^)]+)\)/g
  let i = 0
  let m: RegExpExecArray | null
  while ((m = re.exec(str)) !== null) {
    parts.push(str.slice(i, m.index))
    parts.push(
      <Link
        key={m.index}
        href={m[2]}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sage-600 underline hover:no-underline"
      >
        {m[1]}
      </Link>,
    )
    i = m.index + m[0].length
  }
  parts.push(str.slice(i))
  return parts
}

export const Checkbox: React.FC<
  Omit<CheckboxField, 'label'> & {
    errors: Partial<FieldErrorsImpl>
    label?: React.ReactNode
    register: UseFormRegister<FieldValues>
  }
> = ({ name, defaultValue, errors, label, register, required }) => {
  const props = register(name, { required: required })
  const { setValue, watch } = useFormContext()

  // Respects defaultValue when form hasn't set a value yet (e.g. initial load). When form resets and sets this field to false, watch(name) becomes false and the checkbox clears.

  const watchedValue = watch(name)
  const checked = watchedValue === true || (watchedValue === undefined && defaultValue === true)
  const content = typeof label === 'string' && label.includes('[') ? labelWithLinks(label) : label

  return (
    <>
      <div className={cn('flex items-start', required ? 'gap-4' : 'gap-2')}>
        <CheckboxUi
          checked={checked}
          id={name}
          {...props}
          onCheckedChange={(checkedValue) => {
            setValue(props.name, checkedValue === 'indeterminate' ? false : checkedValue)
          }}
        />
        <Label
          htmlFor={name}
          className="relative flex min-h-8 items-center leading-5 text-card-foreground"
        >
          <span
            className={cn(
              'relative',
              required &&
                'required before:absolute before:-top-0.5 before:-left-0.5 before:-translate-x-full before:content-["*"]',
            )}
          >
            {content}
          </span>
        </Label>
      </div>
      {errors[name] && <Error name={name} />}
    </>
  )
}
